




















































































































































































































































































































































import { VueConstructor } from 'vue';
import { ref, defineComponent, computed, watch } from '@vue/composition-api';
import * as d3 from 'd3-scale-chromatic';
import store from '@/store';
import {
  AnnotationFrame,
  AnnotationFrameReference,
  AnnotationFrameStatus,
  MotionScoreValue,
  MotionScoreValueToColor,
  MotionScoreValueToText,
  MotionScoreMapToNumber,
  FrameNumberToTime,
  MotionScoreNumberToMap,
  MotionActionMap,
} from '@/types';
import { defaultAnnotationClass } from '@/utils';
import { snackbar } from '@/components/GlobalSnackbar';
import actions from '@/controller/actions';
import { updateImageData } from '@/controller/state';
import { initializeTracksFromDataset } from '@/controller/utils';
import { useClient } from '@/use';

interface ClassEntry {
  class: string;
  coordinates: string;
  type: 'box';
  index: number;
}

interface FrameEntry {
  index: number;
  score: string;
  flags: string;
  status: AnnotationFrameStatus;
  reference: AnnotationFrameReference;
}

export default defineComponent({
  name: 'Sidebar',
  setup() {
    // TODO: use ComponentInstance type if @vue/composition-api is updated to >= 1.0.0-beta.26
    const currentStudyType = computed(() => store.state.dataset.studyType);
    const currentDatasetId = computed(() => store.state.dataset.datasetId); // eslint-disable-line no-underscore-dangle
    const frameListTable = ref<InstanceType<VueConstructor> | null>(null);

    const isScorerOrAnnotator = computed(() => store.getters.isScorerOrAnnotator);
    const isSupervisor = computed(() => store.getters.isSupervisor);
    const dataset = computed(() => store.state.dataset.dataset);
    const currentFrameIndex = computed(() => store.state.dataset.currentFrameIndex || 0);
    const currentFrame = computed(() => store.getters.dataset.currentFrame);
    const selectedStudyType = computed(() => store.state.dataset.studyType);
    const annotationClasses = computed(() => store.getters.application.annotationClasses);
    const validAnnotationClasses = computed(() => annotationClasses.value?.filter(
      (cls) => cls !== defaultAnnotationClass.class
    ) || null);

    const scoreClasses = computed(() => store.getters.application.scoreClasses);
    const loading = computed(() => store.state.dataset.loading);

    // Scroll to keep row in frame
    watch(currentFrameIndex, async (frame, oldFrame) => {
      const container: HTMLElement | null | undefined = frameListTable.value?.$el.querySelector('.v-data-table__wrapper');

      // Check if frame manually clicked
      if (Math.abs(frame - oldFrame) > 1) {
        // Scroll to top if dataset was just loaded
        if (currentFrameIndex.value === 0 && container) {
          container.scrollTop = 0;
        }

        return;
      }

      const forward = frame > oldFrame;
      const element: HTMLElement | null | undefined = frameListTable.value?.$el.querySelector('.v-data-table__selected');
      if (container && element) {
        const directionOffset = forward ? element.clientHeight : -element.clientHeight;
        const currentElementOffset = element.offsetTop;
        const containerMidpoint = container.clientHeight / 2;

        container.scrollTop = currentElementOffset - containerMidpoint + directionOffset;
      }
    });

    // Frame List
    const framelistHeaders = ref([
      { text: 'Index', value: 'index' },
      { text: 'Score', value: 'score' },
      { text: 'Annotations', value: 'flags' },
    ]);

    const motionFramelistHeaders = ref([
      { text: 'Start', value: 'startTime' },
      { text: 'End', value: 'endTime' },
      { text: 'Class', value: 'class' },
    ]);

    function frameListItemClass(item: FrameEntry) {
      // Check Status
      switch (item.status) {
        case 'confirmed':
          return 'red--text';
        case 'flagged':
          return 'warning--text';
        default:
          break;
      }

      if (item.reference.scorer === 'model') {
        return 'blue--text';
      }
      return null;
    }

    function classListToFlags(classList: readonly { class: string }[]): string {
      if (validAnnotationClasses.value === null) { return ''; }

      // Mark the class with its first character if present, else '-'
      const classSet = new Set(classList.map((e) => e.class));
      const classMarker = validAnnotationClasses.value.map(
        (cls) => (
          classSet.has(cls)
            ? cls.charAt(0).toUpperCase()
            : '-'
        ),
      );

      return classMarker.join('');
    }

    function scoreMapToString(scoreMap: {[key: string]: number} | undefined): string {
      if (scoreClasses.value === null) {
        return 'N/A';
      }

      // Create an array of the score values, with missing entries marked as '-'
      const values: string[] = scoreMap === undefined
        ? '-'.repeat(scoreClasses.value.length).split('')
        : scoreClasses.value.map(
          (cls) => (scoreMap[cls.class] > -1 ? String(scoreMap[cls.class]) : '-'),
        );

      return `(${values.join(', ')})`;
    }

    const frameListItems = computed(() => {
      if (dataset.value === null) {
        return undefined;
      }

      const maxIndexLength = String(dataset.value.length - 1).length;
      return dataset.value?.map((frame, index) => {
        const motionValue = frame.motion_prediction?.predicted_class;
        const motionAnnotationValue: MotionScoreValue = MotionScoreMapToNumber(frame.score.motion?.actions);
        const startTime = FrameNumberToTime(frame.score.motion?.start_frame!, frame.reference.fps);
        const endTime = FrameNumberToTime(frame.score.motion?.end_frame!, frame.reference.fps);

        return {
          index: String(index).padStart(maxIndexLength, '0'),
          score: scoreMapToString(frame.score['pain-mgs']),
          flags: classListToFlags(frame.annotations.regions),
          status: frame.status,
          reference: frame.reference,
          motionValue,
          motionAnnotationValue,
          motionColor: MotionScoreValueToColor(motionValue),
          motionAnnotationColor: MotionScoreValueToColor(motionAnnotationValue),
          motionAnnotationText: MotionScoreValueToText(motionAnnotationValue),
          startMinute: String(startTime.minutes).padStart(2, '0'),
          startSecond: String(startTime.seconds).padStart(2, '0'),
          endMinute: String(endTime.minutes).padStart(2, '0'),
          endSecond: String(endTime.seconds).padStart(2, '0'),
        };
      });
    });

    const currentFrameListItem = computed(
      () => frameListItems.value?.[currentFrameIndex.value],
    );

    function changeFrame(frame: FrameEntry) {
      actions.changeFrame(frame.index);
    }

    // Class List
    const classListHeaders = ref([
      { text: 'Class', value: 'class' },
      { text: 'Coordinates', value: 'coordinates' },
    ]);

    const classListItems = computed(
      () => currentFrame.value?.annotations.regions.map(
        ({ class: className, coordinates, type }, index: number) => {
          const roundedCoordinates = coordinates.map((coord) => Math.round(coord));

          return {
            class: className,
            coordinates: `(${roundedCoordinates.join(', ')})`,
            type,
            index,
          } as ClassEntry;
        }),
    );

    const currentClassListItem = computed(() => {
      const { currentAnnotationIndex } = store.state.dataset;
      if (currentAnnotationIndex !== null && classListItems.value !== undefined) {
        return classListItems.value[currentAnnotationIndex];
      }

      return undefined;
    });

    function changeAnnotationClass(newClass: string) {
      actions.setAnnotationClass(newClass);
    }

    function selectAnnotation(index: number) {
      actions.setSelectedAnnotation(index, false);
    }

    async function evaluateImage(): Promise<AnnotationFrame | null> {
      const currentFrameFile = currentFrame.value?.file;
      if (currentFrameFile === undefined) {
        return null;
      }

      return useClient().evaluateImage(currentFrameFile);
    }

    function syncDataset() {
      // Need to re-initialize image data, since only the dataset order changed
      updateImageData(dataset.value);

      // Re-initialize tracks to match updated dataset order
      initializeTracksFromDataset();
    }

    function shuffle() {
      store.dispatch.dataset.shuffleDataset();
      syncDataset();
    }

    function sort() {
      store.dispatch.dataset.sortDataset();
      syncDataset();
    }

    function clearAnnotations() {
      actions.clearDatasetAnnotations();
    }

    function clearScores() {
      store.dispatch.dataset.clearDatasetScores();
    }

    const predictModes = ['all', 'scores', 'annotations'] as const;
    const predictMode = ref<typeof predictModes[number]>('annotations');
    async function remotePrediction() {
      if (
        scoreClasses.value === null
        || currentFrame.value === null
        || selectedStudyType.value === null
      ) {
        throw new Error('Missing one of the following: ScoreClasses, CurrentFrame, SelectedStudyType');
      }

      // Get current data before async call, in case it changes
      const frame = currentFrameIndex.value;
      const entireFrameScore = currentFrame.value.score;
      const currentPainScore = currentFrame.value.score['pain-mgs'];

      // Get prediction
      const predictionResp = await evaluateImage();
      if (predictionResp === null) {
        // TODO: Dispatch snackbar and log specific error in console
        console.error('Prediction returned null');
        return;
      }

      const { annotations: { regions: annotations }, score } = predictionResp;
      if (predictMode.value !== 'scores') {
        // Apply new annotations, removing existing ones
        actions.clearAnnotations(frame);
        annotations.forEach((annotation) => {
          actions.addAnnotation(frame, annotation);
        });
      }

      if (predictMode.value !== 'annotations') {
        // Get score from prediction, ensure that selected study type has an entry
        const scorePrediction = score['pain-mgs'];
        if (scorePrediction === undefined) {
          return;
        }

        // Apply new score values over current score
        const newScore = scoreClasses.value.reduce(
          (scoreObj, scoreClass) => ({
            ...scoreObj,
            [scoreClass.class]: scorePrediction[scoreClass.class],
          }),
          currentPainScore,
        );

        const newFrameScore = { ...entireFrameScore, [selectedStudyType.value]: newScore };
        store.commit.dataset.setFrameScore({ frame, score: newFrameScore });
      }

      // Set frame as predicted (must come after clearAnnotations)
      store.dispatch.dataset.setFramePredicted({ index: frame, predicted: true });

      // Dispatch notification
      snackbar.dispatch({
        text: 'Prediction applied',
        buttonIcon: 'mdi-close',
        options: {
          top: true,
          left: true,
          color: 'success',
        },
      });
    }

    const buttons = ref([
      { title: 'Shuffle', action: shuffle },
      { title: 'Sort', action: sort },
      { title: 'Clear All Annotations', action: clearAnnotations },
      { title: 'Clear All Scores', action: clearScores },
    ]);

    // Add dialog
    const addDialogOpen = ref(false);
    const formValid = ref(false);
    const editStartMinute = ref<number | null>(0);
    const editStartSecond = ref<number | null>(0);
    const editEndMinute = ref<number | null>(0);
    const editEndSecond = ref<number | null>(0);
    const min0Max59Rule = (val: number) => (val >= 0 && val <= 59) || 'the number is out of range';
    const selectedMotion = ref({
      score: null,
      text: null,
      color: null,
    });
    const selectedMotionActionMap = ref<MotionActionMap | null>(null);
    const items = [
      {
        score: 0,
        text: 'Sleeping',
        color: d3.schemeCategory10[0],
      },
      {
        score: 1,
        text: 'Awake',
        color: d3.schemeCategory10[2],
      },
      {
        score: 2,
        text: 'Labored',
        color: d3.schemeCategory10[1],
      },
      {
        score: 3,
        text: 'Rearing',
        color: d3.schemeCategory10[3],
      },
      {
        score: 4,
        text: 'Sniffing',
        color: d3.schemeCategory10[4],
      },
      {
        score: 5,
        text: 'Grooming',
        color: d3.schemeCategory10[5],
      },
      {
        score: -1,
        text: 'N/A',
        color: 'lightgray',
      },
    ];

    function openAddDialog() {
      addDialogOpen.value = true;
      const len = frameListItems?.value?.length!;
      const lastDatasetItem = frameListItems?.value ? frameListItems?.value[len - 1] : null;
      editStartMinute.value = Number(lastDatasetItem?.endMinute);
      editStartSecond.value = lastDatasetItem?.endSecond ? Number(lastDatasetItem?.endSecond) + 1 : 0;
      editEndMinute.value = Number(lastDatasetItem?.endMinute);
      editEndSecond.value = editStartSecond.value;
    }
    function closeAddDialog() {
      editStartMinute.value = null;
      editStartSecond.value = null;
      addDialogOpen.value = false;
    }

    function selectMotionClass(motionScore: MotionScoreValue) {
      const motionActionMap = MotionScoreNumberToMap(motionScore);
      selectedMotionActionMap.value = motionActionMap;
    }

    async function saveNewDatasetItem() {
      try {
        const payload = {
          startMinute: editStartMinute.value!,
          startSecond: editStartSecond.value!,
          endMinute: editEndMinute.value!,
          endSecond: editEndSecond.value!,
          actions: selectedMotionActionMap.value,
        };
        // save success popup
        await store.dispatch.dataset.saveNewDatasetItem(payload);
        snackbar.dispatch({
          text: 'New Dataset Item Successfully Saved',
          buttonIcon: 'mdi-close',
          options: {
            top: true,
            color: 'success',
          },
        });
        closeAddDialog();
        await actions.fetchDataset(currentDatasetId.value!, false);
      } catch {
        // save error popup
        snackbar.dispatch({
          text: 'Error saving the dataset item. See console for errors.',
          buttonIcon: 'mdi-close',
          options: {
            top: true,
            color: 'error',
          },
        });
      }
    }
    return {
      isScorerOrAnnotator,
      isSupervisor,
      loading,
      currentStudyType,
      annotationClasses,
      scoreClasses,
      currentFrameListItem,
      frameListTable,
      framelistHeaders,
      motionFramelistHeaders,
      frameListItemClass,
      frameListItems,
      changeFrame,
      classListHeaders,
      classListItems,
      currentClassListItem,
      changeAnnotationClass,
      selectAnnotation,
      remotePrediction,
      predictMode,
      predictModes,
      buttons,
      openAddDialog,
      addDialogOpen,
      closeAddDialog,
      formValid,
      editStartMinute,
      editStartSecond,
      editEndMinute,
      editEndSecond,
      min0Max59Rule,
      selectedMotion,
      selectMotionClass,
      saveNewDatasetItem,
      items,
    };
  },
});
